/**
 * Message Handler Hook
 * Handles incoming messages from BIM software (Revit, etc.)
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  isCoordinates, 
  isCountryInUnitedCountries,
  resolveCoordinates,
  resolveAddressInUnitedCountries 
} from 'utils/geocoding';
import { websocketService } from 'services/websocketService';
import { getElementType } from 'services/apiService';
import { buildCanonicalDebugFixture, buildCanonicalMock, tryResolveElementSlug } from '../mocks/canonicalMocks';
import { FIXTURE_VARIANTS, type FixtureVariant } from '../fixtures/canonical';

const CANONICAL_HOST_CONTRACT_VERSION = 'cem-element-type/1.0';

export function useMessageHandler(
  apiHost: string,
  token?: string,
  isDebug?: boolean
) {
  const [elementId, setElementId] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [placeName, setPlaceName] = useState<string>('');
  const [elementType, setElementType] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [hostVersion, setHostVersion] = useState<string>('1.0.0');
  const [toolsList, setToolsList] = useState<string>('');
  const [elementTypeError, setElementTypeError] = useState<string>('');
  const [isDeterminingElementType, setIsDeterminingElementType] = useState<boolean>(false);
  const [countryCache, setCountryCache] = useState<Map<string, { locality: string; country: string }>>(new Map());
  const elementTypeCache = useRef<Map<string, string>>(new Map());
  const pendingElementTypeRequests = useRef<Map<string, Promise<string>>>(new Map());
  const debugInitialized = useRef<string | null>(null);
  const elementSelectionVersion = useRef(0);
  
  const [searchParams] = useSearchParams();

  /**
   * Debug logging helper
   */
  const log = useCallback((text: string) => {
    if (isDebug) {
      console.log(text);
    }
  }, [isDebug]);

  /**
   * Remove BIMComply internal properties from content
   * These properties are for internal use only and should never be sent to validation requests
   */
  function removeBIMComplyProperties(content: string): string {
    if (!content || typeof content !== 'string') return content;
    
    try {
      // Try to parse as JSON
      const data = JSON.parse(content);
      
      // Recursively remove all BIMComply_* properties from the entire structure
      const cleanData = (obj: any): any => {
        if (Array.isArray(obj)) {
          // Filter arrays to remove objects with BIMComply names and recurse into remaining items
          return obj
            .filter(item => {
              // If it's an object with a Name property starting with BIMComply_, remove it
              if (item && typeof item === 'object' && item.Name && typeof item.Name === 'string') {
                return !item.Name.startsWith('BIMComply_');
              }
              return true;
            })
            .map(item => cleanData(item));
        } else if (obj && typeof obj === 'object') {
          // For objects, remove BIMComply_* keys and recurse into values
          const cleaned: any = {};
          for (const key in obj) {
            if (!key.startsWith('BIMComply_')) {
              cleaned[key] = cleanData(obj[key]);
            }
          }
          return cleaned;
        }
        return obj;
      };
      
      return JSON.stringify(cleanData(data));
    } catch {
      // If not JSON, try to remove BIMComply properties from plain text
      // This handles cases where content might be formatted differently
      const lines = content.split('\n');
      const cleanedLines = lines.filter(line => {
        // Remove lines that start with BIMComply_
        return !line.trim().startsWith('BIMComply_') && 
               !line.includes('"BIMComply_') &&
               !line.includes("'BIMComply_");
      });
      
      return cleanedLines.join('\n');
    }
  }

  /**
   * Resolve element type from category, family, and type
   * Uses cache to avoid repeated API calls
   */
  const resolveElementType = useCallback(async (
    category: string | undefined,
    family: string | undefined,
    type: string | undefined
  ): Promise<string> => {
    if (!category || !family || !type) {
      throw new Error('Element type normalization requires category, family, and type.');
    }

    const cacheKey = `${category}|${family}|${type}`.toLowerCase();
    
    if (elementTypeCache.current.has(cacheKey)) {
      const cachedValue = elementTypeCache.current.get(cacheKey)!;
      log(`Element type resolved from cache: ${cachedValue}`);
      return cachedValue;
    }

    if (pendingElementTypeRequests.current.has(cacheKey)) {
      log(`Element type request already pending for: ${cacheKey}`);
      return pendingElementTypeRequests.current.get(cacheKey)!;
    }

    const request = getElementType(category, family, type, token, apiHost)
      .then(resolvedElementType => {
        const slug = tryResolveElementSlug(resolvedElementType);
        if (!slug) {
          throw new Error(`Unsupported normalized element type "${resolvedElementType}"`);
        }

        const canonicalElementType = buildCanonicalMock(slug, '').defaultElementType;
        elementTypeCache.current.set(cacheKey, canonicalElementType);
        log(`Element type resolved from API: ${canonicalElementType}`);
        return canonicalElementType;
      });
    pendingElementTypeRequests.current.set(cacheKey, request);

    try {
      log(`Calling getElementType API for: ${cacheKey}`);
      return await request;
    } finally {
      pendingElementTypeRequests.current.delete(cacheKey);
    }
  }, [token, apiHost, log]);

  const handleElementTypeError = useCallback((error: unknown) => {
    console.error('Error resolving element type:', error);
    const detail = error instanceof Error ? error.message : 'Unknown normalization error';
    setElementTypeError(`Could not normalize selected element: ${detail}`);
    setIsDeterminingElementType(false);
  }, []);

  /**
   * Extract category, family, and type from JSON content or parsed object
   * and resolve element type using cache/API
   */
  const extractAndResolveElementType = useCallback((
    contentOrJson: string | any,
    rawElementType: string,
    selectionVersion: number
  ) => {
    try {
      let parsed = contentOrJson;
      if (typeof contentOrJson === 'string') {
        try {
          parsed = JSON.parse(contentOrJson);
        } catch {
          parsed = {};
        }
      }
      
      let category = '';
      let family = '';
      let type = '';
          
      // Extract category, family and type from Parameters
      if (parsed.Parameters && Array.isArray(parsed.Parameters)) {
        const categoryParam = parsed.Parameters.find((param: any) => param.Name === 'Category');
        const familyParam = parsed.Parameters.find((param: any) => param.Name === 'Family');
        const typeParam = parsed.Parameters.find((param: any) => param.Name === 'Type');
        if (categoryParam) category = categoryParam.Value;
        if (familyParam) family = familyParam.Value;
        if (typeParam) type = typeParam.Value;
      }
      
      console.warn('[telemetry] element_type_normalization_fallback', {
        contractVersion: 'legacy',
        canonicalType: 'unknown',
        reason: 'unversioned_or_noncanonical_envelope',
        correlationId: String(selectionVersion),
      });
      resolveElementType(
        category || rawElementType,
        family || rawElementType,
        type || rawElementType
      ).then(resolvedElementType => {
        if (elementSelectionVersion.current !== selectionVersion) {
          return;
        }
        setElementType(resolvedElementType);
        setElementTypeError('');
        setIsDeterminingElementType(false);
      }).catch(error => {
        if (elementSelectionVersion.current === selectionVersion) {
          handleElementTypeError(error);
        }
      });
    } catch (error) {
      handleElementTypeError(error);
    }
  }, [handleElementTypeError, resolveElementType]);

  useEffect(() => {
    function onReceivedMessage(event: MessageEvent) {
            const hostSelectionMessage =
                event.data === "" ||
                event.data?.type === "ElementId" ||
                event.data?.type === "Source" ||
                event.data?.type === "Country" ||
                (typeof event.data === "string" && event.data.indexOf("Content") > 0);
            if (isDebug && hostSelectionMessage) {
                return;
            }

            if (event.data === "") {
                setContent('');
            }
            // Handle FunctionCall response from host
            else if (event.data.type === "FunctionCallResponse") {
                try {
                    const { tool_use_id, result, error } = event.data.value;
                    
                    // Send response back to server via WebSocket
                    websocketService.sendFunctionCallResponse({
                        requestId: tool_use_id,
                        result,
                        error
                    });
                } catch (err) {
                    console.error('Failed to handle FunctionCall response:', err);
                }
            }
            else if (event.data.type === "ElementId" ||
                event.data.type === "Source" ||
                event.data.type === "Country" ||
                event.data.type === "HostVersion" ||
                event.data.type === "ToolsList" ||
                event.data.toString().indexOf("Content") > 0) {
                
                if (event.data.type === "ElementId") {
                    setElementId(event.data.value);
                }
                else if (event.data.type === "Source") {
                    setSource(event.data.value);
                }
                else if (event.data.type === "HostVersion") {
                    setHostVersion(event.data.value);
                }
                else if (event.data.type === "ToolsList") {
                    setToolsList(event.data.value);
                }
                else if (event.data.type === "Country") {
                    const value = event.data.value;
                    
                    if (searchParams.get("debugCountry") === "true") {
                        alert('Country: ' + value);
                        console.error('Country: ', value);                
                    }
                    
                    if (isCoordinates(value)) {
                        resolveCoordinates(
                            value, 
                            apiHost, 
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setCountry(location.country);
                        });
                    }
                    else if (isCountryInUnitedCountries(value)) {
                        resolveAddressInUnitedCountries(
                            value,
                            apiHost,
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setCountry(location.country);
                        });
                    }
                    else {
                        setCountry(value);
                    }
                }
                else {
                    var obj = JSON.parse(event.data);
                    
                    // Remove BIMComply internal properties before setting content
                    const cleanedContent = removeBIMComplyProperties(obj.Content);
                    log(cleanedContent);
                    const selectionVersion = ++elementSelectionVersion.current;
                    
                    if (obj.ContractVersion) {
                        try {
                            if (obj.ContractVersion !== CANONICAL_HOST_CONTRACT_VERSION) {
                                throw new Error('unsupported canonical contract version');
                            }

                            const canonicalSlug = tryResolveElementSlug(obj.ElementType);
                            if (!canonicalSlug) {
                                throw new Error('unsupported canonical ElementType');
                            }

                            const mock = buildCanonicalMock(canonicalSlug, '');
                            if (obj.ElementType !== mock.defaultElementType) {
                                throw new Error('ElementType must use the exact canonical label');
                            }

                            const canonicalContent = JSON.parse(cleanedContent);
                            if (
                                !canonicalContent ||
                                typeof canonicalContent.id !== 'string' ||
                                canonicalContent.type !== mock.payload.type ||
                                !canonicalContent.properties ||
                                typeof canonicalContent.properties !== 'object' ||
                                Array.isArray(canonicalContent.properties)
                            ) {
                                throw new Error('ElementType does not match the canonical Content.type');
                            }

                            setContent(cleanedContent);
                            setElementType(mock.defaultElementType);
                            setElementTypeError('');
                            setIsDeterminingElementType(false);
                        } catch (error) {
                            console.error('Invalid canonical element envelope:', error);
                            const reason = error instanceof Error ? error.message : 'invalid canonical content';
                            setElementTypeError(`Invalid canonical element envelope: ${reason}`);
                            setIsDeterminingElementType(false);
                        }
                    } else {
                        setContent(cleanedContent);
                        setIsDeterminingElementType(true);
                        extractAndResolveElementType(cleanedContent, obj.ElementType, selectionVersion);
                    }
                }
            }
        }

        if (isDebug) {
            const debugKey = searchParams.toString();
            if (debugInitialized.current !== debugKey) {
                debugInitialized.current = debugKey;
                elementSelectionVersion.current += 1;

                setElementId("a5f8b706-9c55-4a4a-bde0-935bb5cfa11")
                setSource("Revit");

                const urlCountry = searchParams.get("country");
                if (urlCountry) {
                    if (isCoordinates(urlCountry)) {
                        resolveCoordinates(
                            urlCountry,
                            apiHost,
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setCountry(location.country);
                        });
                    }
                    else if (isCountryInUnitedCountries(urlCountry)) {
                        resolveAddressInUnitedCountries(
                            urlCountry,
                            apiHost,
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setCountry(location.country);
                        });
                    }
                    else {
                        setCountry(urlCountry);
                    }
                }

                const urlLocation = searchParams.get("location");
                if (urlLocation) {
                    if (isCoordinates(urlLocation)) {
                        resolveCoordinates(
                            urlLocation,
                            apiHost,
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setPlaceName(`${location.locality}, ${location.country}`);
                        });
                    }
                    else if (isCountryInUnitedCountries(urlLocation)) {
                        resolveAddressInUnitedCountries(
                            urlLocation,
                            apiHost,
                            token,
                            countryCache,
                            setCountryCache
                        ).then(location => {
                            setPlaceName(`${location.locality}, ${location.country}`);
                        });
                    }
                    else {
                        setPlaceName(urlLocation);
                    }
                }

                const requestedElementType = searchParams.get("elementType");
                if (requestedElementType && !tryResolveElementSlug(requestedElementType)) {
                    setContent('');
                    setElementType('');
                    setElementTypeError(`Unsupported elementType "${requestedElementType}"`);
                    setIsDeterminingElementType(false);
                } else {
                    setElementTypeError('');

                    // Debug-only fixture selection: honour ?variant= and
                    // ?schemaVersion= when the registry has the element type
                    // registered. Falls back to the legacy canonical mock so
                    // pre-existing debug URLs continue to work unchanged.
                    const requestedVariant = searchParams.get("variant");
                    const requestedSchemaVersion = searchParams.get("schemaVersion") ?? undefined;
                    let variant: FixtureVariant | null = null;
                    let variantError = '';
                    if (requestedVariant) {
                        if (!FIXTURE_VARIANTS.includes(requestedVariant as FixtureVariant)) {
                            variantError = `Unsupported fixture variant "${requestedVariant}". Expected one of: ${FIXTURE_VARIANTS.join(', ')}.`;
                        } else {
                            variant = requestedVariant as FixtureVariant;
                        }
                    } else if (requestedSchemaVersion) {
                        variant = 'compliant';
                    }

                    if (variantError) {
                        setContent('');
                        setElementType('');
                        setElementTypeError(variantError);
                        setIsDeterminingElementType(false);
                    } else {
                        const fixture = variant
                            ? buildCanonicalDebugFixture(requestedElementType, variant, {
                                  schemaVersion: requestedSchemaVersion,
                              })
                            : null;

                        if (fixture) {
                            setContent(fixture.content);
                            setElementType(fixture.defaultElementType);
                            setIsDeterminingElementType(false);
                        } else {
                            const mock = buildCanonicalMock(requestedElementType, placeName);
                            setContent(mock.content);
                            setElementType(mock.defaultElementType);
                            setIsDeterminingElementType(false);
                        }
                    }
                }
            }
        }

        window.addEventListener("message", onReceivedMessage);

        return function () {
            window.removeEventListener("message", onReceivedMessage);
        };
    }, [searchParams, placeName, countryCache, apiHost, token, isDebug, log, extractAndResolveElementType]);

  return {
    elementId,
    source,
    country,
    placeName,
    elementType,
    elementTypeError,
    content,
    setContent,
    isDeterminingElementType,
    hostVersion,
    toolsList
  };
}
